import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import FilterContext from './filter-context';
import type { FilterOption, FilterState } from './types';
import { FilterFieldNames } from './types';
import useApiCall from 'src/hooks/use-api-call';
import { debounce } from 'lodash';

export const useFilterContext = () => {
  const { userFilters, setUserFilters, loading, entries, activeFilters, filterNumber, applyFilters, setApplyFilters } =
    useContext(FilterContext);

  const resetFilters = useCallback(
    (filtersToReset?: string[]) => {
      if (filtersToReset?.length) {
        let filtersResetted = { ...userFilters };
        filtersToReset.forEach((filterName) => {
          if (filtersResetted[filterName]) {
            delete filtersResetted[filterName];
          }
        });
        setUserFilters({ ...filtersResetted });
      } else {
        setUserFilters({});
      }
      setApplyFilters(true);
    },
    [setUserFilters],
  );

  const resetFilter = useCallback(
    (inputs: string[]) => {
      setUserFilters((currentValues) => {
        let newValues = { ...currentValues };
        inputs.forEach((input) => {
          newValues[input] && delete newValues[input];
        });
        return newValues;
      });
    },
    [setUserFilters],
  );

  const discardFilterChanges = useCallback(
    (snapshot: FilterState) => {
      setUserFilters((currentValues) => {
        // search filter should not be discarded
        const currentSearch = currentValues.search;

        return {
          ...snapshot,
          ...(currentSearch ? { search: currentSearch } : {}),
        };
      });
    },
    [setUserFilters],
  );

  return {
    userFilters,
    setUserFilters,
    resetFilters,
    resetFilter,
    discardFilterChanges,
    activeFilters,
    filterNumber,
    entries,
    loading,
    applyFilters,
    setApplyFilters,
  };
};

const getDynamicFieldName: (values: any, defaultField: string, fieldNames?: FilterFieldNames) => string = (
  values,
  defaultField,
  fieldNames,
) => {
  if (fieldNames && fieldNames.length) {
    const firstConditionHit = fieldNames.find(({ name, condition }) => condition(values));

    if (firstConditionHit) {
      return firstConditionHit.name;
    }
  }

  return defaultField;
};

export const useFilterOption: (
  filterOption: FilterOption,
  defaultValue: any,
) => {
  value: any;
  setValue: (value: any) => void;
} = (filterOption, defaultValue = []) => {
  const { userFilters, setUserFilters } = useFilterContext();

  const setValue = (value: any) => {
    const fieldName = getDynamicFieldName(value, filterOption.defaultField, filterOption.fieldNames);
    setUserFilters((currentValues) => ({
      ...currentValues,
      [filterOption.input]: { fieldName, value },
    }));
  };

  return {
    value: userFilters[filterOption.input]?.value === undefined ? defaultValue : userFilters[filterOption.input]?.value,
    setValue,
  };
};

export const useFilterValue = (inputName: string) => {
  const { userFilters } = useFilterContext();
  return userFilters[inputName] ? userFilters[inputName].value : undefined;
};

export const useMultiSelectSearch = <Data>({
  apiFetch,
  searchFieldName,
  processFilterData,
}: {
  apiFetch: any;
  processFilterData?: (data: Data[]) => { value: keyof Data; label: string; name: string }[];
  searchFieldName?: keyof Data;
}) => {
  const [data, setData] = useState<Data[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [hasSearchQuery, setHasSearchQuery] = useState(false);
  const { withApi, loading } = useApiCall();

  const dataFilter = useMemo(
    () =>
      data.map((dataVal: Data) => ({
        value: dataVal['id' as keyof Data] || dataVal,
        label: dataVal['name' as keyof Data] || dataVal,
        name: dataVal['name' as keyof Data] || dataVal,
      })),
    [data],
  );

  const fetchData = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await apiFetch({
          limit: 50,
          field: searchFieldName || 'name',
          icontains: searchQuery,
        });
        const data = response?.results || response || [];
        setData(data.filter((object: Data) => !!object && object !== '-'));
        setHasSearchQuery(!!searchQuery?.length);
      });
    },
    [withApi, apiFetch, searchFieldName],
  );

  const searchData = useCallback(
    (value: string) => {
      setIsTyping(false);
      fetchData(value);
    },
    [fetchData],
  );

  const debouncedDataSearch = useMemo(() => debounce(searchData, 500), [searchData]);

  const onSearch = useCallback(
    (value: string) => {
      setIsTyping((prev) => (prev ? prev : true));
      debouncedDataSearch(value);
    },
    [debouncedDataSearch],
  );

  useEffect(() => {
    fetchData();
  }, []);

  return {
    dataFilter: processFilterData ? processFilterData(data) : dataFilter,
    onSearch,
    hasSearchQuery,
    loading: isTyping || loading,
  };
};
